import React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { GooglePace } from "../hooks/useGoogleReport";
import { campaignStack, stackEdges, type StackInput, type StackRow } from "../utils/campaignStack";
import type { CampaignLane } from "../utils/dayMarkers";
import { MONTH_SHORT } from "../utils/metricChange";
import { CampaignLanes } from "./CampaignLanes";
import { StackLegend, StackTooltip } from "./CampaignStackParts";
import { DayPips, type DayPip } from "./DayPips";

interface PaceChartProps {
  pace: GooglePace;
  /** The headline metric, plural: "actions", "sessions", "clicks". */
  label: string;
  /** This month's line, in the source's colour. */
  color: string;
  year: number;
  month: number;
  /** A complete month's label sets the two whole months side by side, as
   *  its tiles do; one in progress compares the same days. */
  complete: boolean;
  /** The headline metric over the same-days windows, the pair the tiles
   *  compare; the label of a month in progress reads it. */
  toDate: { through: string; current: number | null; previous: number | null } | null;
  /** The month's marked days, pips on the x axis at this month's days. */
  pips?: ReadonlyArray<DayPip>;
  /** This month's campaigns, each its measure by day: the running total is
   *  drawn as a stack of their running totals in place of the line, last
   *  month's dashed total on top. Without a daily row in the month, the line. */
  campaigns?: ReadonlyArray<StackInput>;
  /** Campaign lanes under the axis. */
  lanes?: { lanes: ReadonlyArray<CampaignLane>; hidden: number };
}

interface PaceRow {
  day: number;
  current: number | null;
  previous: number | null;
  stack: StackRow | null;
}

// Last month is the reference, so it stays grey and dashed behind this
// month's line; the dash keeps the two apart without colour.
const PREVIOUS_COLOR = "#9CA3AF";
const AXIS_COLOR = "#E5E7EB";
const GRID_COLOR = "#F3F4F6";
const TICK = { fontSize: 10, fill: "#9CA3AF" };
// The pips sit on the plot's x axis line, so they read these two.
const Y_AXIS_WIDTH = 32;
const X_AXIS_HEIGHT = 30;

const daysIn = (year: number, month: number) => new Date(year, month, 0).getDate();

const formatCount = (n: number | null): string => (n == null ? "-" : n.toLocaleString());

const formatTick = (n: number): string =>
  n >= 1_000 ? `${Math.round(n / 100) / 10}K` : String(n);

/** The month's running total by day against the month before's, on the same
 *  day axis: the gap between the lines on a day is the same-days comparison.
 *  This month's line stops at the source's settled day. */
export const PaceChart: React.FC<PaceChartProps> = ({
  pace,
  label,
  color,
  year,
  month,
  complete,
  toDate,
  pips,
  campaigns,
  lanes,
}) => {
  if (pace.current.length === 0 && pace.previous.length === 0) return null;

  const prevYear = month === 1 ? year - 1 : year;
  const prevMonth = month === 1 ? 12 : month - 1;
  const lastDay = Math.max(daysIn(year, month), daysIn(prevYear, prevMonth));
  const current = new Map(pace.current.map((p) => [p.day, p.value]));
  const previous = new Map(pace.previous.map((p) => [p.day, p.value]));
  const settledDay = pace.current[pace.current.length - 1]?.day ?? 0;
  const stack = campaigns
    ? campaignStack(campaigns, { year, month, lastDay, cumulative: true, throughDay: settledDay })
    : null;
  const dayRow = (day: number, stackRow: StackRow | null): PaceRow => ({
    day,
    current: current.get(day) ?? null,
    previous: previous.get(day) ?? null,
    stack: stackRow,
  });
  const rows: PaceRow[] = Array.from({ length: lastDay }, (_, i) => dayRow(i + 1, stack?.rows[i] ?? null));
  const rowByDay = new Map(rows.map((row) => [row.day, row]));
  // The chart also draws the rows that square a campaign's start and end.
  const chartRows = stack ? stackEdges(stack.rows).map((s) => dayRow(s.day, s)) : rows;

  // The direct label: on a complete month the two whole months; in progress
  // the same-days pair the tiles compare, never the pace endpoints, which in
  // late March would set 30 days against February's 28.
  const latest = pace.current[pace.current.length - 1];
  const previousTotal = pace.previous[pace.previous.length - 1]?.value ?? null;
  const throughDay = toDate ? Number(toDate.through.slice(8, 10)) : null;
  const thisMonth = MONTH_SHORT[month - 1];
  const lastMonth = MONTH_SHORT[prevMonth - 1];

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <span className="text-[12px] font-medium text-gray-500">
          {label}, running total
        </span>
        <span className="text-[12px] text-gray-500 tabular-nums truncate">
          {latest && complete ? (
            <>
              <span className="font-medium text-gray-900">{latest.value.toLocaleString()}</span>
              {` ${thisMonth} vs ${formatCount(previousTotal)} ${lastMonth}`}
            </>
          ) : !complete && toDate ? (
            <>
              <span className="font-medium text-gray-900">{formatCount(toDate.current)}</span>
              {" vs "}
              {formatCount(toDate.previous)} by day {throughDay}
            </>
          ) : (
            "no settled days yet"
          )}
        </span>
      </div>
      <div className="flex items-center gap-3 mb-1 text-[11px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 border-t-2" style={{ borderColor: color }} />
          {thisMonth}
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-3 border-t-2 border-dashed"
            style={{ borderColor: PREVIOUS_COLOR }}
          />
          {lastMonth}
        </span>
      </div>
      <div className="relative" style={{ height: 168 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartRows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis
              dataKey="day"
              type="number"
              domain={[1, lastDay]}
              ticks={[1, 10, 20, lastDay]}
              height={X_AXIS_HEIGHT}
              tick={TICK}
              tickLine={false}
              axisLine={{ stroke: AXIS_COLOR }}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickCount={3}
              tick={TICK}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickFormatter={formatTick}
            />
            <RechartsTooltip
              cursor={{ stroke: AXIS_COLOR }}
              content={({ active, label: day }) => {
                const row = active ? rowByDay.get(Number(day)) : undefined;
                if (row && stack && row.stack && row.stack.total != null) {
                  return (
                    <StackTooltip
                      row={row.stack}
                      layers={stack.layers}
                      format={(n) => n.toLocaleString()}
                      heading={`day ${row.day}`}
                      totalLabel={thisMonth}
                      footer={
                        <div className="flex justify-between gap-2 text-gray-300">
                          <span>{lastMonth}</span>
                          <span>{formatCount(row.previous)}</span>
                        </div>
                      }
                    />
                  );
                }
                return row ? (
                  <div className="rounded-md bg-gray-900 text-white text-[11px] px-2 py-1 shadow-md tabular-nums">
                    <span className="text-gray-300">day {row.day}:</span>{" "}
                    <span className="font-medium">{formatCount(row.current)}</span>
                    <span className="text-gray-300"> vs {formatCount(row.previous)}</span>
                  </div>
                ) : null;
              }}
            />
            {stack ? (
              stack.layers.map((layer) => (
                <Area
                  key={layer.key}
                  dataKey={(row: PaceRow) => row.stack?.values[layer.key] ?? null}
                  name={layer.name}
                  stackId="campaigns"
                  type="linear"
                  stroke="#FFFFFF"
                  strokeWidth={1.5}
                  fill={layer.color}
                  fillOpacity={1}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ))
            ) : (
              <Line
                dataKey="current"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 4, stroke: "#FFFFFF", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            )}
            {/* After this month's areas, so last month's line stays on top. */}
            <Line
              dataKey="previous"
              stroke={PREVIOUS_COLOR}
              strokeWidth={2}
              strokeDasharray="4 3"
              strokeLinecap="round"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        {pips && pips.length > 0 && (
          <DayPips
            pips={pips}
            year={year}
            month={month}
            domain={[1, lastDay]}
            inset={{ left: Y_AXIS_WIDTH, right: 4, bottom: X_AXIS_HEIGHT }}
          />
        )}
      </div>
      {lanes && (
        <CampaignLanes
          lanes={lanes.lanes}
          hidden={lanes.hidden}
          domain={[1, lastDay]}
          inset={{ left: Y_AXIS_WIDTH, right: 4 }}
        />
      )}
      {stack && <StackLegend layers={stack.layers} format={(n) => n.toLocaleString()} />}
    </div>
  );
};
